import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ContentContext } from "../contexts/ContentContext";
import LanguageSelector from "./LanguageSelector";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import BrandMark from "./BrandMark";

export default function Header() {
    const { data } = useContext(ContentContext);
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const [openMobileSub, setOpenMobileSub] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const menuRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setOpenMenu(null);
        setOpenMobileSub(null);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const goToSection = (to, id) => {
        setOpenMenu(null);
        setOpen(false);
        setOpenMobileSub(null);
        if (location.pathname === to) {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            navigate(`${to}#${id}`, { replace: true });
        } else {
            navigate(`${to}#${id}`);
        }
    };

    const nav = [
        { to: "/", label: data.header.home },
        { to: "/about", label: data.header.about },
        { to: "/products", label: data.header.products || "Products" },
        { to: "/sectors", label: data.header.sectors, items: data.sectors },
        { to: "/focus", label: data.header.focus, items: data.focus },
        { to: "/partners", label: data.header.partners },
        { to: "/contact", label: data.header.contact },
    ];

    return (
        <header
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-md shadow-soft"
                    : "bg-white/80 backdrop-blur-sm"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" className="flex items-center gap-3 group">
                        <BrandMark className="h-12 w-12 transition-transform group-hover:scale-105" />
                        <div className="hidden sm:block leading-tight">
                            <div className="font-serif text-lg font-bold text-navy-900 tracking-wide">
                                {data.brand.name}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="h-px w-6 bg-gold-500" />
                                <span className="text-[10px] text-navy-700 font-semibold tracking-[0.18em] uppercase">
                                    {data.brand.shortTagline}
                                </span>
                            </div>
                        </div>
                    </Link>

                    <nav ref={menuRef} className="hidden lg:flex items-center gap-1">
                        {nav.map((item) =>
                            item.items ? (
                                <div
                                    key={item.to}
                                    className="relative"
                                    onMouseEnter={() => setOpenMenu(item.to)}
                                    onMouseLeave={() => setOpenMenu(null)}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setOpenMenu(openMenu === item.to ? null : item.to)
                                        }
                                        className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                                            location.pathname === item.to
                                                ? "text-gold-600"
                                                : "text-navy-700 hover:text-gold-600"
                                        }`}
                                        aria-haspopup="true"
                                        aria-expanded={openMenu === item.to}
                                    >
                                        {item.label}
                                        <FiChevronDown
                                            className={`w-4 h-4 transition-transform ${
                                                openMenu === item.to ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    {openMenu === item.to && (
                                        <div className="absolute top-full start-0 pt-3 w-72 z-50">
                                            <div className="bg-white rounded-xl shadow-card border border-navy-100 overflow-hidden">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setOpenMenu(null);
                                                        navigate(item.to);
                                                    }}
                                                    className="w-full text-start px-4 py-3 bg-navy-900 text-white hover:bg-navy-800 transition"
                                                >
                                                    <div className="text-[10px] text-gold-300 uppercase tracking-widest font-semibold mb-0.5">
                                                        View all
                                                    </div>
                                                    <div className="text-sm font-bold">{item.label}</div>
                                                </button>
                                                <div className="divide-y divide-navy-50">
                                                    {item.items.map((sub, idx) => (
                                                        <button
                                                            key={sub.id}
                                                            type="button"
                                                            onClick={() => goToSection(item.to, sub.id)}
                                                            className="group w-full text-start px-4 py-3 flex items-center gap-3 hover:bg-navy-50 transition"
                                                        >
                                                            <span className="text-[11px] font-mono font-semibold text-gold-600 w-5">
                                                                0{idx + 1}
                                                            </span>
                                                            <span className="text-sm text-navy-800 group-hover:text-gold-700 font-medium flex-1">
                                                                {sub.title}
                                                            </span>
                                                            <span className="text-navy-300 group-hover:text-gold-600 rtl:rotate-180 transition">
                                                                →
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `px-4 py-2 rounded-lg text-sm font-medium transition ${
                                            isActive
                                                ? "text-gold-600"
                                                : "text-navy-700 hover:text-gold-600"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            )
                        )}
                    </nav>

                    <div className="flex items-center gap-2">
                        <LanguageSelector />
                        <Link
                            to="/contact"
                            className="hidden md:inline-flex items-center px-5 py-2.5 gradient-emerald text-white text-sm font-semibold rounded-lg transition shadow-soft hover:shadow-glow hover:brightness-110"
                        >
                            {data.header.contact}
                        </Link>
                        <button
                            className="lg:hidden p-2 text-navy-900"
                            onClick={() => setOpen(!open)}
                            aria-label="Menu"
                        >
                            {open ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-white border-t border-navy-100 shadow-soft">
                    <nav className="flex flex-col py-2 px-6">
                        {nav.map((item) =>
                            item.items ? (
                                <div key={item.to} className="border-b border-navy-50">
                                    <div className="flex items-center">
                                        <NavLink
                                            to={item.to}
                                            className={({ isActive }) =>
                                                `flex-1 py-3 text-base font-medium ${
                                                    isActive ? "text-gold-600" : "text-navy-800"
                                                }`
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenMobileSub(
                                                    openMobileSub === item.to ? null : item.to
                                                )
                                            }
                                            className="p-3 text-navy-600"
                                            aria-label={`Toggle ${item.label}`}
                                        >
                                            <FiChevronDown
                                                className={`w-5 h-5 transition-transform ${
                                                    openMobileSub === item.to ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                    </div>
                                    {openMobileSub === item.to && (
                                        <div className="pb-3 ps-3 flex flex-col">
                                            {item.items.map((sub) => (
                                                <button
                                                    key={sub.id}
                                                    type="button"
                                                    onClick={() => goToSection(item.to, sub.id)}
                                                    className="text-start py-2 text-sm text-navy-700 hover:text-gold-600 transition"
                                                >
                                                    {sub.title}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        `py-3 text-base font-medium border-b border-navy-50 ${
                                            isActive ? "text-gold-600" : "text-navy-800"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            )
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
}
